import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Shield, Plus, Trash2, Edit2, Loader2, Save, X,
  Star, Gift, Trophy, Target, Zap, Crown, Award,
  CheckCircle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const BADGE_ICONS = [
  { id: 'star', icon: Star, name: 'Estrella' },
  { id: 'gift', icon: Gift, name: 'Regalo' },
  { id: 'trophy', icon: Trophy, name: 'Trofeo' },
  { id: 'target', icon: Target, name: 'Meta' },
  { id: 'zap', icon: Zap, name: 'Rayo' },
  { id: 'crown', icon: Crown, name: 'Corona' },
  { id: 'award', icon: Award, name: 'Premio' },
  { id: 'shield', icon: Shield, name: 'Escudo' },
];

const CRITERIA_TYPES = [
  { id: 'constancias_count', name: 'Cantidad de constancias', description: 'Usuario tiene X constancias' },
  { id: 'recognitions_count', name: 'Cantidad de reconocimientos', description: 'Usuario tiene X reconocimientos' },
  { id: 'first_constancia', name: 'Primera constancia', description: 'Usuario obtiene su primera constancia' },
  { id: 'first_recognition', name: 'Primer reconocimiento', description: 'Usuario obtiene su primer reconocimiento' },
];

const BADGE_CATEGORIES = [
  { id: 'learning', name: 'Aprendizaje', color: '#8b5cf6' },
  { id: 'achievement', name: 'Logro', color: '#f59e0b' },
  { id: 'milestone', name: 'Hito', color: '#6366f1' },
  { id: 'special', name: 'Especial', color: '#10b981' },
  { id: 'engagement', name: 'Compromiso', color: '#3b82f6' },
];

const DEFAULT_COLORS = [
  '#6366f1', '#8b5cf6', '#d946ef', '#f59e0b', '#eab308',
  '#10b981', '#3b82f6', '#ef4444', '#d4af37', '#ec4899'
];

export default function AdminBadgesPage() {
  const [badges, setBadges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingBadge, setEditingBadge] = useState(null);
  const [saving, setSaving] = useState(false);
  const token = localStorage.getItem('upim_token');

  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    icon: 'star',
    color: '#6366f1',
    category: 'achievement',
    criteria_type: 'constancias_count',
    criteria_value: 1,
    points: 10,
    is_active: true,
    is_secret: false,
  });

  useEffect(() => {
    loadBadges();
  }, []);

  const loadBadges = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/api/badges/admin/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBadges(res.data.badges || []);
    } catch (err) {
      setError('Error al cargar los badges');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (badge = null) => {
    if (badge) {
      setEditingBadge(badge);
      setFormData({
        name: badge.name,
        slug: badge.slug || '',
        description: badge.description || '',
        icon: badge.icon || 'star',
        color: badge.color || '#6366f1',
        category: badge.category || 'achievement',
        criteria_type: badge.criteria_type || 'constancias_count',
        criteria_value: badge.criteria_value || 1,
        points: badge.points || 10,
        is_active: badge.is_active !== false,
        is_secret: badge.is_secret || false,
      });
    } else {
      setEditingBadge(null);
      setFormData({
        name: '',
        slug: '',
        description: '',
        icon: 'star',
        color: '#6366f1',
        category: 'achievement',
        criteria_type: 'constancias_count',
        criteria_value: 1,
        points: 10,
        is_active: true,
        is_secret: false,
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingBadge(null);
  };

  const handleSaveBadge = async () => {
    try {
      setSaving(true);
      const headers = { Authorization: `Bearer ${token}` };

      if (editingBadge) {
        await axios.put(`${API_URL}/api/badges/admin/${editingBadge.id}`, formData, { headers });
      } else {
        await axios.post(`${API_URL}/api/badges/admin/create`, formData, { headers });
      }

      await loadBadges();
      handleCloseModal();
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al guardar el badge');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteBadge = async (badgeId) => {
    if (!window.confirm('¿Estás seguro de eliminar este badge?')) return;

    try {
      await axios.delete(`${API_URL}/api/badges/admin/${badgeId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await loadBadges();
    } catch (err) {
      setError('Error al eliminar el badge');
    }
  };

  const getIconComponent = (iconId) => {
    const found = BADGE_ICONS.find(i => i.id === iconId);
    return found ? found.icon : Star;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Administrar Badges</h1>
            <p className="text-gray-500 mt-1">Crear y gestionar insignias gamificadas</p>
          </div>
          <button
            onClick={() => handleOpenModal()}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
          >
            <Plus className="w-5 h-5" />
            Nuevo Badge
          </button>
        </div>

        {error && (
          <div className="mb-4 p-4 bg-red-100 text-red-700 rounded-lg flex items-center justify-between">
            <span>{error}</span>
            <button onClick={() => setError(null)}><X className="w-4 h-4" /></button>
          </div>
        )}

        {/* Badges Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {badges.map(badge => {
            const IconComponent = getIconComponent(badge.icon);
            return (
              <div
                key={badge.id}
                className={`bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow ${
                  !badge.is_active ? 'opacity-60' : ''
                }`}
              >
                <div
                  className="p-4 text-white"
                  style={{ backgroundColor: badge.color || '#6366f1' }}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                      <IconComponent className="w-5 h-5" />
                    </div>
                    <div className="flex gap-1">
                      {badge.is_secret && (
                        <span className="px-2 py-1 bg-white/20 rounded text-xs">Secreto</span>
                      )}
                      {!badge.is_active && (
                        <span className="px-2 py-1 bg-white/20 rounded text-xs">Inactivo</span>
                      )}
                    </div>
                  </div>
                  <h3 className="font-bold">{badge.name}</h3>
                  <p className="text-white/80 text-sm truncate">{badge.description}</p>
                </div>

                <div className="p-4">
                  <div className="flex flex-wrap gap-2 text-xs text-gray-500 mb-3">
                    <span className="px-2 py-1 bg-gray-100 rounded">{badge.category}</span>
                    <span className="px-2 py-1 bg-gray-100 rounded">+{badge.points} pts</span>
                    <span className="px-2 py-1 bg-gray-100 rounded">
                      {badge.criteria_type}: {badge.criteria_value}
                    </span>
                  </div>

                  <div className="flex gap-2">
                    <button
                      onClick={() => handleOpenModal(badge)}
                      className="flex-1 flex items-center justify-center gap-1 px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                    >
                      <Edit2 className="w-4 h-4" />
                      Editar
                    </button>
                    <button
                      onClick={() => handleDeleteBadge(badge.id)}
                      className="px-3 py-2 text-sm bg-red-100 text-red-600 rounded-lg hover:bg-red-200"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {badges.length === 0 && (
          <div className="text-center py-16 bg-white rounded-xl">
            <Gift className="w-16 h-16 mx-auto text-gray-300 mb-4" />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">
              No hay badges creados
            </h3>
            <p className="text-gray-500 mb-4">
              Crea tu primer badge para empezar
            </p>
            <button
              onClick={() => handleOpenModal()}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
            >
              Crear Badge
            </button>
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-bold">
                  {editingBadge ? 'Editar Badge' : 'Nuevo Badge'}
                </h2>
                <button onClick={handleCloseModal}>
                  <X className="w-6 h-6 text-gray-400" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-4">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nombre del Badge *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="Ej: Primera Constancia"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Descripción
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  rows={2}
                  placeholder="Descripción del badge..."
                />
              </div>

              {/* Icon & Color */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Icono
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {BADGE_ICONS.map(({ id, icon: Icon }) => (
                      <button
                        key={id}
                        type="button"
                        onClick={() => setFormData({ ...formData, icon: id })}
                        className={`p-2 rounded-lg border-2 transition-all ${
                          formData.icon === id
                            ? 'border-indigo-500 bg-indigo-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <Icon className="w-5 h-5" style={{ color: formData.color }} />
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Color
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {DEFAULT_COLORS.map(color => (
                      <button
                        key={color}
                        type="button"
                        onClick={() => setFormData({ ...formData, color })}
                        className={`w-8 h-8 rounded-lg border-2 transition-all ${
                          formData.color === color ? 'border-gray-900 scale-110' : 'border-transparent'
                        }`}
                        style={{ backgroundColor: color }}
                      />
                    ))}
                  </div>
                </div>
              </div>

              {/* Category */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Categoría
                </label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  {BADGE_CATEGORIES.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
              </div>

              {/* Criteria */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Criterio
                  </label>
                  <select
                    value={formData.criteria_type}
                    onChange={(e) => setFormData({ ...formData, criteria_type: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    {CRITERIA_TYPES.map(ct => (
                      <option key={ct.id} value={ct.id}>{ct.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Valor Requerido
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={formData.criteria_value}
                    onChange={(e) => setFormData({ ...formData, criteria_value: parseInt(e.target.value) || 1 })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
              </div>

              {/* Points */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Puntos que otorga
                </label>
                <input
                  type="number"
                  min="1"
                  value={formData.points}
                  onChange={(e) => setFormData({ ...formData, points: parseInt(e.target.value) || 1 })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              {/* Flags */}
              <div className="flex gap-6">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    className="rounded text-indigo-600 focus:ring-indigo-500"
                  />
                  <span className="text-sm text-gray-700">Activo</span>
                </label>

                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.is_secret}
                    onChange={(e) => setFormData({ ...formData, is_secret: e.target.checked })}
                    className="rounded text-indigo-600 focus:ring-indigo-500"
                  />
                  <span className="text-sm text-gray-700">Secreto (oculto hasta obtenerlo)</span>
                </label>
              </div>

              {/* Preview */}
              <div className="border-t pt-4">
                <p className="text-sm font-medium text-gray-700 mb-2">Vista previa:</p>
                <div
                  className="rounded-xl p-4 text-white"
                  style={{ backgroundColor: formData.color }}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
                      {(() => {
                        const IconComp = getIconComponent(formData.icon);
                        return <IconComp className="w-6 h-6" />;
                      })()}
                    </div>
                    <div>
                      <h4 className="font-bold">{formData.name || 'Nombre del Badge'}</h4>
                      <p className="text-white/80 text-sm">{formData.description || 'Descripción...'}</p>
                      <p className="text-sm mt-1">+{formData.points} puntos</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="p-6 border-t bg-gray-50 flex gap-3 justify-end">
              <button
                onClick={handleCloseModal}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-100"
              >
                Cancelar
              </button>
              <button
                onClick={handleSaveBadge}
                disabled={!formData.name || saving}
                className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {editingBadge ? 'Guardar Cambios' : 'Crear Badge'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
